import * as _ from 'lodash';
import { useCallback, useRef } from 'react';
import type { MouseEvent, VoidFunctionComponent } from 'react';
import type { NodeRendererProps } from 'react-arborist';

import Button from '../../generic/Button';
import { FileTreeNode } from '../../generic/FileTree';
import { icons } from '../../generic/Icon';
import { Menu, MenuDivider, MenuItem } from '../../generic/Menu';

import type { SnippetTreeNodeData } from './types';

/**
 * SnippetsTreeView - Empty component for displaying snippets in a tree view.
 */
const SnippetsTreeViewNode: VoidFunctionComponent<NodeRendererProps<SnippetTreeNodeData>> = ({
  node,
  style,
  tree,
}) => {
  const isDirectory = node.data.type === 'directory';
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  // Function to check if the click event was on the menu button
  const isMenuButtonClick = useCallback((event: MouseEvent) =>
    menuButtonRef.current?.contains(event.target as Node), [menuButtonRef]);

  //  Handle clicking on the entire row.
  const handleClick = (event: MouseEvent) => {
    //  Ignore clicks if they are just opening the menu
    if (isMenuButtonClick(event)) {
      return;
    }

    //  Handle selecting a node.
    node.handleClick(event);

    //  "isInternal" is a react-arborist-ism for "this is a directory".
    //  Calling `node.toggle` will toggle the open/closed state of these nodes.
    //  Clicking the entire row should only open directories, as we want to make it easier
    //  for users to see the contents of folders. They can use the CollapseAll menu option to collapse them later.
    if (node.isInternal && !node.isOpen) {
      node.toggle();
    }
  };

  return (
    <FileTreeNode
      style={style}
      onClick={handleClick}
      isSelected={node.isSelected}
    >
      <FileTreeNode.Arrow isOpen={node.isOpen} isDirectory={isDirectory} />
      <FileTreeNode.Icon isDirectory={isDirectory} />
      <FileTreeNode.Text text={node.data.name} />
      {node.isSelected && (
        <FileTreeNode.Rest>
          <Menu
            direction="left"
            onClick={(event: MouseEvent) => {
              //  We need to prevent clicks on the menu from automatically collapsing the tree.
              event.stopPropagation();
            }}
            menuButton={
              <span className="margin-right-xs">
                <Button
                  ref={menuButtonRef}
                  type="text"
                  size="micro"
                  icon={icons.ellipsisHorizontal}
                  onClick={_.noop}
                />
              </span>
            }
          >
            {isDirectory && (
              <>
                <MenuItem onClick={() => tree.close(node.data.path)}>
                  Collapse All
                </MenuItem>
                <MenuDivider />
              </>
            )}
            <MenuItem>
              Reject All
            </MenuItem>
          </Menu>
        </FileTreeNode.Rest>
      )}
    </FileTreeNode>
  );
};

export default SnippetsTreeViewNode;
